import type {
  Point,
  RowState,
  CellState,
  CellRole,
  CellScope,
  RowGroup,
  TableState,
  CellIdentifier,
} from "./types";
import {
  checkSelectionIsRectangular,
  checkSelectionSpansSingleRow,
  checkSelectionSpansSingleCol,
  checkSelectionIsFullHeight,
  checkSelectionIsFullWidth,
} from "./calculations";
import { decodeCellIdentifier, encodeCellIdentifier } from "./utils";

export const selectCells = (
  table: TableState,
  start: Point,
  end: Point,
): TableState => {
  const selectionTop = Math.min(start.y, end.y);
  const selectionLeft = Math.min(start.x, end.x);
  const selectionRight = Math.max(start.x, end.x);
  const selectionBottom = Math.max(start.y, end.y);

  const newCells = table.cells.map((cell) => {
    const cellTop = table.rows
      .slice(0, cell.originRow)
      .reduce((acc, row) => acc + row.height, 0);

    const cellLeft = table.cols
      .slice(0, cell.originCol)
      .reduce((acc, col) => acc + col.width, 0);

    const cellRight =
      cellLeft +
      table.cols
        .slice(cell.originCol, cell.originCol + cell.colSpan)
        .reduce((acc, col) => acc + col.width, 0);

    const cellBottom =
      cellTop +
      table.rows
        .slice(cell.originRow, cell.originRow + cell.rowSpan)
        .reduce((acc, row) => acc + row.height, 0);

    const rectsAreIntersecting =
      cellLeft <= selectionRight &&
      cellRight >= selectionLeft &&
      cellTop <= selectionBottom &&
      cellBottom >= selectionTop;

    return { ...cell, selected: rectsAreIntersecting };
  });

  return { ...table, cells: newCells };
};

export const toggleCellSelection = (
  table: TableState,
  cellIdentifier: CellIdentifier,
): TableState => {
  const newCells = table.cells.map((cell) => {
    if (
      cell.originCol === cellIdentifier.originCol &&
      cell.originRow === cellIdentifier.originRow
    ) {
      return { ...cell, selected: !cell.selected };
    }
    return cell;
  });

  return { ...table, cells: newCells };
};

export const moveX = (
  table: TableState,
  x: number,
  absolute: boolean = true,
): TableState => {
  const finalX = Math.max(absolute ? x : table.position.x + x, 0);
  return {
    ...table,
    position: { ...table.position, x: finalX },
  };
};

export const moveY = (
  table: TableState,
  y: number,
  absolute: boolean = true,
): TableState => {
  const finalY = Math.max(absolute ? y : table.position.y + y, 0);
  return {
    ...table,
    position: { ...table.position, y: finalY },
  };
};

export const mergeSelectedCells = (table: TableState): TableState => {
  if (!checkSelectionIsRectangular(table.cells)) return table;

  const selectedCells = table.cells.filter((cell) => cell.selected);

  const minRow = Math.min(...selectedCells.map((cell) => cell.originRow));
  const maxRow = Math.max(
    ...selectedCells.map((cell) => cell.originRow + cell.rowSpan - 1),
  );
  const minCol = Math.min(...selectedCells.map((cell) => cell.originCol));
  const maxCol = Math.max(
    ...selectedCells.map((cell) => cell.originCol + cell.colSpan - 1),
  );

  const hasHeaderCells = selectedCells.some((cell) => cell.role === "th");

  const newCell: CellState = {
    originRow: minRow,
    originCol: minCol,
    rowSpan: maxRow - minRow + 1,
    colSpan: maxCol - minCol + 1,
    selected: true,
    role: hasHeaderCells ? "th" : "td",
  };

  const newCells = table.cells.filter((cell) => !cell.selected).concat(newCell);
  const modifiedTableState = { ...table, cells: newCells };
  return dissolveEmptyRowAndCols(modifiedTableState);
};

export const dissolveEmptyRowAndCols = (table: TableState): TableState => {
  const newRows = [...table.rows];
  const newCols = [...table.cols];
  const newCells = [...table.cells];

  for (let rowIndex = newRows.length - 1; rowIndex >= 0; rowIndex--) {
    const rowCells = newCells.filter((cell) => cell.originRow === rowIndex);

    if (rowCells.length > 0) {
      continue;
    }

    const rowIndexAbove = rowIndex - 1;
    const rowIndexBelow = rowIndex + 1;
    const rowHeight = newRows[rowIndex].height;

    if (rowIndexAbove >= 0) {
      newRows[rowIndexAbove].height += rowHeight;
    } else if (rowIndexBelow < newRows.length) {
      newRows[rowIndexBelow].height += rowHeight;
    }

    newRows.splice(rowIndex, 1);

    newRows.forEach((row) => {
      if (row.index > rowIndex) {
        row.index -= 1;
      }
    });

    newCells.forEach((cell) => {
      if (cell.originRow > rowIndex) {
        cell.originRow -= 1;
      } else if (cell.originRow + cell.rowSpan - 1 >= rowIndex) {
        cell.rowSpan -= 1;
      }
    });
  }

  for (let colIndex = newCols.length - 1; colIndex > 0; colIndex--) {
    const colCells = newCells.filter((cell) => cell.originCol === colIndex);

    if (colCells.length > 0) {
      continue;
    }

    const colIndexBefore = colIndex - 1;
    const colIndexAfter = colIndex + 1;
    const colWidth = newCols[colIndex].width;

    if (colIndexBefore >= 0) {
      newCols[colIndexBefore].width += colWidth;
    } else if (colIndexAfter < newCols.length) {
      newCols[colIndexAfter].width += colWidth;
    }

    newCols.splice(colIndex, 1);

    newCols.forEach((col) => {
      if (col.index > colIndex) {
        col.index -= 1;
      }
    });

    newCells.forEach((cell) => {
      if (cell.originCol > colIndex) {
        cell.originCol -= 1;
      } else if (cell.originCol + cell.colSpan - 1 >= colIndex) {
        cell.colSpan -= 1;
      }
    });
  }

  return { ...table, rows: newRows, cols: newCols, cells: newCells };
};

export const splitSelectedCells = (table: TableState): TableState => {
  const selectedCells = table.cells.filter((cell) => cell.selected);
  const unselectedCells = table.cells.filter((cell) => !cell.selected);
  const newCells: CellState[] = [];

  for (const cell of selectedCells) {
    for (let row = 0; row < cell.rowSpan; row++) {
      for (let col = 0; col < cell.colSpan; col++) {
        newCells.push({
          originRow: cell.originRow + row,
          originCol: cell.originCol + col,
          rowSpan: 1,
          colSpan: 1,
          selected: true,
          role: cell.role,
        });
      }
    }
  }

  return { ...table, cells: unselectedCells.concat(newCells) };
};

export const resizeRow = (
  table: TableState,
  rowIndex: number,
  newHeight: number,
): TableState => {
  const newTableState = { ...table };
  newTableState.rows[rowIndex].height = Math.max(newHeight, 8);
  return newTableState;
};

export const resizeCol = (
  table: TableState,
  colIndex: number,
  newWidth: number,
): TableState => {
  const newTableState = { ...table };
  newTableState.cols[colIndex].width = Math.max(newWidth, 8);
  return newTableState;
};

export const divideRow = (table: TableState, divisor: number): TableState => {
  if (
    !checkSelectionIsFullWidth(table.cells, table.cols) ||
    !checkSelectionSpansSingleRow(table.cells)
  ) {
    return table;
  }

  const selectedCells = table.cells.filter((cell) => cell.selected);
  const selectedRowIndex = selectedCells[0].originRow;

  const updatedRows = table.rows.map((row) => {
    if (row.index > selectedRowIndex) {
      return { ...row, index: row.index + divisor - 1 };
    } else {
      return row;
    }
  });

  updatedRows.splice(
    selectedRowIndex,
    1,
    ...Array.from({ length: divisor }, (_, index) => ({
      height: table.rows[selectedRowIndex].height / divisor,
      index: selectedRowIndex + index,
      group: table.rows[selectedRowIndex].group,
    })),
  );

  const updatedCells = table.cells
    .map((cell) => {
      if (cell.originRow > selectedRowIndex) {
        return { ...cell, originRow: cell.originRow + divisor - 1 };
      } else if (cell.originRow + cell.rowSpan - 1 >= selectedRowIndex) {
        return { ...cell, rowSpan: cell.rowSpan + divisor - 1 };
      } else {
        return cell;
      }
    })
    .filter((cell) => !cell.selected);

  const newCells = updatedCells.concat(
    ...selectedCells.map((cell) => {
      return Array.from({ length: divisor }, (_, index) => ({
        originRow: cell.originRow + index,
        originCol: cell.originCol,
        rowSpan: 1,
        colSpan: cell.colSpan,
        selected: true,
        role: cell.role,
      }));
    }),
  );

  return { ...table, rows: updatedRows, cells: newCells };
};

export const divideCol = (table: TableState, divisor: number): TableState => {
  if (
    !checkSelectionIsFullHeight(table.cells, table.rows) ||
    !checkSelectionSpansSingleCol(table.cells)
  ) {
    return table;
  }

  const selectedCells = table.cells.filter((cell) => cell.selected);
  const selectedColIndex = selectedCells[0].originCol;

  const updatedCols = table.cols.map((col) => {
    if (col.index > selectedColIndex) {
      return { ...col, index: col.index + divisor - 1 };
    } else {
      return col;
    }
  });

  updatedCols.splice(
    selectedColIndex,
    1,
    ...Array.from({ length: divisor }, (_, index) => ({
      width: table.cols[selectedColIndex].width / divisor,
      index: selectedColIndex + index,
    })),
  );

  const updatedCells = table.cells
    .map((cell) => {
      if (cell.originCol > selectedColIndex) {
        return { ...cell, originCol: cell.originCol + divisor - 1 };
      } else if (cell.originCol + cell.colSpan - 1 >= selectedColIndex) {
        return { ...cell, colSpan: cell.colSpan + divisor - 1 };
      } else {
        return cell;
      }
    })
    .filter((cell) => !cell.selected);

  const newCells = updatedCells.concat(
    ...selectedCells.map((cell) => {
      return Array.from({ length: divisor }, (_, index) => ({
        originRow: cell.originRow,
        originCol: cell.originCol + index,
        rowSpan: cell.rowSpan,
        colSpan: 1,
        selected: true,
        role: cell.role,
      }));
    }),
  );

  return { ...table, cols: updatedCols, cells: newCells };
};

export const deleteSelectedRows = (table: TableState): TableState => {
  if (
    !checkSelectionIsFullWidth(table.cells, table.cols) ||
    checkSelectionIsFullHeight(table.cells, table.rows)
  ) {
    return table;
  }

  const selectedCells = table.cells.filter((cell) => cell.selected);
  const minSelectedRow = Math.min(
    ...selectedCells.map((cell) => cell.originRow),
  );
  const maxSelectedRow = Math.max(
    ...selectedCells.map((cell) => cell.originRow + cell.rowSpan - 1),
  );
  const numRowsToDelete = maxSelectedRow - minSelectedRow + 1;

  const newCells = table.cells
    .filter(
      (cell) =>
        cell.originRow < minSelectedRow || cell.originRow > maxSelectedRow,
    )
    .map((cell) => {
      if (minSelectedRow === 0 && cell.originRow === maxSelectedRow + 1) {
        return {
          ...cell,
          originRow: 0,
          rowSpan: cell.rowSpan + numRowsToDelete - 1,
        };
      } else if (cell.originRow == minSelectedRow - 1) {
        return { ...cell, rowSpan: cell.rowSpan + numRowsToDelete - 1 };
      } else {
        return cell;
      }
    });

  return dissolveEmptyRowAndCols({ ...table, cells: newCells });
};

export const deleteSelectedCols = (table: TableState): TableState => {
  if (
    !checkSelectionIsFullHeight(table.cells, table.rows) ||
    checkSelectionIsFullWidth(table.cells, table.cols)
  ) {
    return table;
  }

  const selectedCells = table.cells.filter((cell) => cell.selected);
  const minSelectedCol = Math.min(
    ...selectedCells.map((cell) => cell.originCol),
  );
  const maxSelectedCol = Math.max(
    ...selectedCells.map((cell) => cell.originCol + cell.colSpan - 1),
  );
  const numColsToDelete = maxSelectedCol - minSelectedCol + 1;

  const newCells = table.cells
    .filter(
      (cell) =>
        cell.originCol < minSelectedCol || cell.originCol > maxSelectedCol,
    )
    .map((cell) => {
      if (minSelectedCol === 0 && cell.originCol === maxSelectedCol + 1) {
        return {
          ...cell,
          originCol: 0,
          colSpan: cell.colSpan + numColsToDelete - 1,
        };
      } else if (cell.originCol == minSelectedCol - 1) {
        return { ...cell, colSpan: cell.colSpan + numColsToDelete - 1 };
      } else {
        return cell;
      }
    });

  return dissolveEmptyRowAndCols({ ...table, cells: newCells });
};

export const updateRoleOfSelectedCells = (
  table: TableState,
  role: CellRole,
): TableState => {
  const newCells = table.cells.map((cell) => {
    if (!cell.selected) {
      return cell;
    }

    if (role === "td") {
      return { ...cell, role, scope: undefined };
    }

    return { ...cell, role };
  });

  return { ...table, cells: newCells };
};

export const updateScopeOfSelectedCells = (
  table: TableState,
  scope: CellScope,
): TableState => {
  const newCells = table.cells.map((cell) => {
    if (cell.selected && cell.role === "th") {
      return { ...cell, scope };
    } else {
      return cell;
    }
  });

  return { ...table, cells: newCells };
};

export const updateGroupOfSelectedCells = (
  table: TableState,
  group: RowGroup,
): TableState => {
  const selectedCells = table.cells.filter((cell) => cell.selected);

  if (selectedCells.length === 0) return table;

  const minRowIndex = Math.min(...selectedCells.map((cell) => cell.originRow));
  const maxRowIndex = Math.max(
    ...selectedCells.map((cell) => cell.originRow + cell.rowSpan - 1),
  );

  if (group === "thead") {
    const newRows = table.rows.map((row) => {
      if (row.index <= maxRowIndex) {
        return { ...row, group: "thead" } as RowState;
      } else if (row.group === "thead") {
        return { ...row, group: "tbody" } as RowState;
      } else {
        return row;
      }
    });

    return { ...table, rows: newRows };
  }

  if (group === "tbody") {
    const newRows = table.rows.map((row) => {
      if (
        (row.index < minRowIndex && row.group === "tfoot") ||
        (row.index > maxRowIndex && row.group === "thead") ||
        (row.index >= minRowIndex && row.index <= maxRowIndex)
      ) {
        return { ...row, group: "tbody" } as RowState;
      } else {
        return row;
      }
    });

    return { ...table, rows: newRows };
  }

  if (group === "tfoot") {
    const newRows = table.rows.map((row) => {
      if (row.index >= minRowIndex) {
        return { ...row, group: "tfoot" } as RowState;
      } else if (row.group === "tfoot") {
        return { ...row, group: "tbody" } as RowState;
      } else {
        return row;
      }
    });

    return { ...table, rows: newRows };
  }

  return table;
};

export const updateHeadersOfSelectedCells = (
  table: TableState,
  addedEncodedHeaders: string[],
  removedEncodedHeaders: string[],
): TableState => {
  const newCells = table.cells.map((cell) => {
    if (cell.selected) {
      const existingHeaders = cell.headers || [];
      const existingEncodedHeaders = existingHeaders.map(encodeCellIdentifier);
      const updatedEncodedHeaders = existingEncodedHeaders
        .filter((header) => !removedEncodedHeaders.includes(header))
        .concat(
          addedEncodedHeaders.filter(
            (header) => !existingEncodedHeaders.includes(header),
          ),
        );

      const updatedDecodedHeaders =
        updatedEncodedHeaders.map(decodeCellIdentifier);

      return { ...cell, headers: updatedDecodedHeaders };
    }
    return cell;
  });

  return { ...table, cells: newCells };
};
